// createDatabase — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-database' })` op (DSE2 §3.G1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice G1 — CREATE / ALTER / DROP DATABASE UI.
//
// Per-engine surface (per DSE2 dialect.database):
//   - mssql:    CREATE DATABASE name [CONTAINMENT = NONE|PARTIAL]
//                 [ON [PRIMARY] (NAME = …, FILENAME = '…') [, …]]
//                 [LOG ON (…)] [COLLATE name]
//   - postgres: CREATE DATABASE [IF NOT EXISTS] name
//                 [WITH OWNER = … TEMPLATE = … ENCODING = '…'
//                  LC_COLLATE = '…' LC_CTYPE = '…' TABLESPACE = …
//                  CONNECTION LIMIT = n]
//   - mysql:    CREATE {DATABASE|SCHEMA} [IF NOT EXISTS] name
//                 [CHARACTER SET = …] [COLLATE = …]
//   - oracle:   CREATE PLUGGABLE DATABASE name ADMIN USER u IDENTIFIED BY p
//                 [FILE_NAME_CONVERT = (…)]
//                 (CDB-level CREATE DATABASE is out of scope per DSE2.)
//   - sqlite:   unsupported — sqlite databases are filesystem files;
//               use ATTACH DATABASE from the connection layer.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DatabaseDef, DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateDatabaseArgs {
  readonly database: DatabaseDef;
}

export function buildCreateDatabaseEdit(args: CreateDatabaseArgs): DdlEdit {
  return { op: 'create-database', database: args.database };
}

export async function commitCreateDatabase(
  ctx: VqeDdlEditContext,
  args: CreateDatabaseArgs,
): Promise<void> {
  const edit = buildCreateDatabaseEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const DATABASE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'mysql', 'oracle',
] as const);

export function isCreateDatabaseSupported(engine: SqlEngine): boolean {
  return DATABASE_SUPPORTED.has(engine);
}

export function getCreateDatabaseUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite databases are filesystem files. Use ATTACH DATABASE from the '
      + 'connection layer to attach an additional database, or open a '
      + 'connection to the file directly.'
    );
  }
  return '';
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths. Returns
 *  `null` when args are acceptable, else a user-facing message. */
export function validateCreateDatabaseForEngine(
  engine: SqlEngine,
  args: CreateDatabaseArgs,
): string | null {
  if (!isCreateDatabaseSupported(engine)) {
    return getCreateDatabaseUnsupportedTooltip(engine);
  }
  const db = args.database;
  if (db.name.trim().length === 0) {
    return 'Database name is required.';
  }
  if (engine === 'mssql') {
    if (db.ifNotExists) {
      return 'mssql does not support IF NOT EXISTS on CREATE DATABASE.';
    }
    if (hasAnyPgsql(db)) return 'mssql does not accept pgsql-only fields on CREATE DATABASE.';
    if (hasAnyMysql(db)) return 'mssql does not accept mysql-only fields on CREATE DATABASE.';
    if (hasAnyOracle(db)) return 'mssql does not accept oracle-only fields on CREATE DATABASE.';
  }
  if (engine === 'postgres') {
    if (hasAnyMssql(db)) return 'postgres does not accept mssql-only fields on CREATE DATABASE.';
    if (hasAnyMysql(db)) return 'postgres does not accept mysql-only fields on CREATE DATABASE.';
    if (hasAnyOracle(db)) return 'postgres does not accept oracle-only fields on CREATE DATABASE.';
  }
  if (engine === 'mysql') {
    if (hasAnyMssql(db)) return 'mysql does not accept mssql-only fields on CREATE DATABASE.';
    if (hasAnyPgsql(db)) return 'mysql does not accept pgsql-only fields on CREATE DATABASE.';
    if (hasAnyOracle(db)) return 'mysql does not accept oracle-only fields on CREATE DATABASE.';
  }
  if (engine === 'oracle') {
    if (db.ifNotExists) {
      return 'oracle does not support IF NOT EXISTS on CREATE DATABASE.';
    }
    if (hasAnyMssql(db)) return 'oracle does not accept mssql-only fields on CREATE DATABASE.';
    if (hasAnyPgsql(db)) return 'oracle does not accept pgsql-only fields on CREATE DATABASE.';
    if (hasAnyMysql(db)) return 'oracle does not accept mysql-only fields on CREATE DATABASE.';
    if (db.oracleAdminUser === undefined || db.oracleAdminUser.trim().length === 0
      || db.oracleAdminPassword === undefined || db.oracleAdminPassword.trim().length === 0) {
      return (
        'Oracle CREATE PLUGGABLE DATABASE requires both an admin user and password.'
      );
    }
  }
  return null;
}

function hasAnyMssql(db: DatabaseDef): boolean {
  return db.mssqlContainment !== undefined
    || db.mssqlDataFiles !== undefined
    || db.mssqlOnPrimary !== undefined
    || db.mssqlLogFiles !== undefined
    || db.mssqlCollate !== undefined;
}

function hasAnyPgsql(db: DatabaseDef): boolean {
  return db.pgsqlOwner !== undefined || db.pgsqlTemplate !== undefined
    || db.pgsqlEncoding !== undefined || db.pgsqlLcCollate !== undefined
    || db.pgsqlLcCtype !== undefined || db.pgsqlTablespace !== undefined
    || db.pgsqlConnectionLimit !== undefined;
}

function hasAnyMysql(db: DatabaseDef): boolean {
  return db.mysqlSchema !== undefined
    || db.mysqlCharacterSet !== undefined
    || db.mysqlCollate !== undefined;
}

function hasAnyOracle(db: DatabaseDef): boolean {
  return db.oracleAdminUser !== undefined
    || db.oracleAdminPassword !== undefined
    || db.oracleFileNameConvertText !== undefined;
}
